import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { CheckCircle2, Loader2 } from 'lucide-react';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
      } else {
        setStatus('pending');
        // Retry after 2 seconds
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setStatus('error');
    }
  };

  if (status === 'loading' || status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Ödemeniz Kontrol Ediliyor</h2>
            <p className="text-slate-600">Lütfen bekleyin...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-red-600">Bir Hata Oluştu</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-600 mb-6">
              Ödeme durumunuz kontrol edilemedi. Lütfen daha sonra tekrar deneyin.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              Ana Sayfaya Dön
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      <Card className="w-full max-w-md shadow-xl border-emerald-200">
        <CardHeader className="text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <CardTitle className="text-3xl text-emerald-700">Ödeme Başarılı!</CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-4">
          <p className="text-slate-700 text-lg">
            Aboneliğiniz başarıyla aktif edildi.
          </p>
          
          {paymentDetails && (
            <div className="bg-emerald-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Ödeme Detayları</p>
              <p className="text-lg font-semibold text-emerald-700">
                {paymentDetails.amount_total / 100} {paymentDetails.currency?.toUpperCase()}
              </p>
            </div>
          )}
          
          <p className="text-slate-600 text-sm">
            Artık tüm premium özelliklere erişebilirsiniz. Mizaç360'ın tüm özelliklerinden faydalanmaya başlayabilirsiniz!
          </p>
          
          <div className="pt-4 space-y-3">
            <Button 
              onClick={() => navigate('/')} 
              className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
            >
              Panelime Git
            </Button>
            
            <Button 
              onClick={() => navigate('/pricing')} 
              variant="outline"
              className="w-full"
            >
              Fiyatlandırmayı Görüntüle
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
